import { jwtDecode } from 'jwt-decode';
import { secureStorage } from './secureStorage';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export interface DecodedToken {
  exp: number;
  sub: string;
  email: string;
  name: string;
  is_platform_admin: boolean;
  role: string;
  email_verified: boolean;
  clubs?: string[];
  login_mode?: 'pilot' | 'club_manager';  // How the user logged in (for dual-role users)
  available_roles?: ('admin' | 'club_manager' | 'pilot')[];  // All roles this user can access
}

export const setTokens = (accessToken: string, refreshToken: string) => {
  secureStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  secureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessToken = (): string | null => {
  return secureStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return secureStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearTokens = () => {
  // Clear platform tokens
  secureStorage.removeItem(ACCESS_TOKEN_KEY);
  secureStorage.removeItem(REFRESH_TOKEN_KEY);
  
  // Clear service authentication
  localStorage.removeItem('serviceToken');
  localStorage.removeItem('serviceUserData');
  localStorage.removeItem('serviceAuthenticated');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userPin');
  
  // Clear any other auth-related data
  localStorage.removeItem('pendingClubRegistration');
  sessionStorage.removeItem('serviceLoginInProgress');
  
  // Dispatch event to notify all listeners
  window.dispatchEvent(new Event('serviceAuthChanged'));
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

export const getDecodedToken = (): DecodedToken | null => {
  const token = getAccessToken();
  if (!token) return null;

  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

export const getUserRole = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.role || null;
};

export const isAdmin = (): boolean => {
  const decoded = getDecodedToken();
  return decoded?.is_platform_admin === true || decoded?.role === 'platform_admin';
};

export const isClubUser = (): boolean => {
  const decoded = getDecodedToken();
  return decoded?.role === 'club' || (decoded?.clubs && decoded.clubs.length > 0) || false;
};

export const isVisitor = (): boolean => {
  return getUserRole() === 'visitor';
};